import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { monthLabel, fmtTons } from "@/lib/format";
import { PlantResults } from "./ResultsView";
import { ArrowLeft } from "lucide-react";

const when = (iso) => new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });

export const SavedReports = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/verification/reports").then((r) => setList(r.data)); }, []);
  const view = async (id) => setOpen((await api.get(`/verification/reports/${id}`)).data);

  if (open) {
    return (
      <div data-testid="saved-report-view" className="space-y-4">
        <Button variant="outline" data-testid="saved-report-back" onClick={() => setOpen(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <div className="text-sm text-slate-600">{monthLabel(open.month)} · Approved by {open.approved_by} on {when(open.created_at)}{open.remarks && ` · ${open.remarks}`}</div>
        {Object.entries(open.results).map(([p, d]) => <PlantResults key={p} plant={p} data={d} readOnly />)}
        {open.decisions?.length > 0 && (
          <section data-testid="report-decisions" className="bg-white border rounded-xl p-4">
            <h3 className="font-semibold mb-2">Corrections & decisions ({open.decisions.length})</h3>
            <ul className="text-sm divide-y">{open.decisions.map((d) => (
              <li key={d.id} className="py-1.5">{d.plant} · {d.date} · <b>{d.customer}</b> · book {d.book_tons ?? "—"} t / website {d.web_tons ?? "—"} t → <b>{d.action.replace(/_/g, " ")}</b> ({d.status}) by {d.approved_by}</li>
            ))}</ul>
          </section>
        )}
      </div>
    );
  }
  return (
    <div data-testid="saved-reports-list" className="bg-white border rounded-xl divide-y">
      {list.length === 0 && <p className="p-4 text-sm text-slate-400">No saved verification reports yet.</p>}
      {list.map((r) => (
        <button key={r.id} data-testid={`saved-report-${r.id}`} onClick={() => view(r.id)} className="w-full text-left p-4 hover:bg-slate-50">
          <div className="font-semibold">{monthLabel(r.month)} — {r.plants.join(" & ")}</div>
          <div className="text-xs text-slate-500">Approved by {r.approved_by} · {when(r.created_at)}{r.remarks && ` · ${r.remarks}`}</div>
          <div className="text-xs mt-1 flex flex-wrap gap-3">
            {Object.entries(r.summary).map(([p, s]) => (
              <span key={p}>{p}: <b className="text-emerald-700">{s.matched} matched</b>, <b className="text-red-700">{s.mismatch} mismatch</b>, {s.missing_on_website + s.missing_in_book} missing, {s.needs_check} check</span>
            ))}
          </div>
        </button>
      ))}
    </div>
  );
};

const fmtVal = (k, v) => (v == null || v === "" ? "—" : k === "tons" ? fmtTons(v) : String(v));

export const AuditHistory = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/verification/audit").then((r) => setList(r.data)); }, []);
  return (
    <div data-testid="audit-history" className="bg-white border rounded-xl overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="p-2">When</th><th className="p-2">Admin</th><th className="p-2">Plant</th><th className="p-2">Sale</th><th className="p-2">Changes</th><th className="p-2">Reason</th></tr></thead>
        <tbody>
          {list.map((a) => (
            <tr key={a.id} data-testid={`audit-row-${a.id}`} className="border-b align-top">
              <td className="p-2 whitespace-nowrap">{when(a.changed_at)}</td><td className="p-2">{a.admin_name}</td><td className="p-2">{a.plant}</td>
              <td className="p-2 whitespace-nowrap">{a.customer_name || a.original.customer_name}<div className="text-xs text-slate-500">{a.sale_date || a.original.sale_date}</div></td>
              <td className="p-2">{a.changed_fields.map((k) => (
                <div key={k}><span className="text-slate-500">{k}:</span> <span className="line-through text-red-700">{fmtVal(k, a.original[k])}</span> → <span className="text-emerald-700 font-semibold">{fmtVal(k, a.updated[k])}</span></div>
              ))}</td>
              <td className="p-2 text-slate-600">{a.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {list.length === 0 && <p className="p-4 text-sm text-slate-400">No website entries have been changed from Book Verification.</p>}
    </div>
  );
};
